import { HeartHandshake, MapPin, ShieldCheck, Timer } from "lucide-react";
import { PageHead } from "@/components/PageHead";
import { Reveal } from "@/components/Reveal";
import { CtaSection } from "@/components/CtaSection";
import { CONTACT, IMAGES, STEPS } from "@/lib/data";

const VALUES = [
  {
    icon: HeartHandshake,
    title: "Honest advice",
    desc: "If you don't need it, we won't sell it. Our recommendations are sized to your real business, not our invoice.",
  },
  {
    icon: Timer,
    title: "Responsive support",
    desc: "A 15-minute average triage on urgent issues, and you always speak to someone who knows your setup.",
  },
  {
    icon: ShieldCheck,
    title: "Security-first mindset",
    desc: "Every decision we make starts with keeping your data safe. Then we make it fast and simple.",
  },
  {
    icon: MapPin,
    title: "Local and accountable",
    desc: "Based in the Central Belt, on site when it matters. You'll know our names, and we'll know yours.",
  },
];

export default function About() {
  return (
    <>
      <PageHead
        eyebrow="About us"
        title="Built on honesty, local commitment and craft."
        sub="Groundbreaking IT was founded to bridge the gap between impersonal corporate providers and unreliable one-person IT help."
        testid="about-page-title"
      />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 sm:pb-24">
        <Reveal>
          <div className="max-w-3xl">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Who we are</h2>
            <div className="mt-5 space-y-4 text-base leading-relaxed text-muted-foreground">
              <p>
                We started Groundbreaking IT because too many small businesses were
                stuck between two bad options: faceless providers with ticket queues
                and contracts written to confuse, or well-meaning freelancers who
                disappear when things get serious.
              </p>
              <p>
                We're a new company, and we're honest about that. What we offer
                instead of decades-old brand recognition is something harder to
                find: direct access to senior engineers, advice in everyday words,
                and a process you can see from day one.
              </p>
              <p>
                Every client gets the same promise: clear communication, fast
                responses, and technology that quietly does its job so you can do
                yours.
              </p>
            </div>
          </div>
        </Reveal>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.07}>
              <div data-testid={`value-${v.title.toLowerCase().replace(/\s+/g, "-")}`} className="h-full rounded-2xl border border-border bg-card p-6 transition-all duration-300 hover:-translate-y-0.5 hover:border-primary/40">
                <v.icon className="h-5 w-5 text-primary" />
                <h3 className="mt-3 font-semibold tracking-tight">{v.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{v.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section data-testid="about-process-section" className="py-16 sm:py-24 bg-muted/40 border-y border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl mb-12">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-primary mb-4">What to expect</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
              How a partnership with us unfolds
            </h2>
          </Reveal>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-5">
            {STEPS.map((s, i) => (
              <Reveal key={s.step} delay={i * 0.08}>
                <div className="border-t-2 border-border pt-6">
                  <p className="font-mono text-3xl font-semibold text-primary/70">{s.step}</p>
                  <h3 className="mt-3 text-lg font-semibold tracking-tight">{s.name}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="relative overflow-hidden rounded-3xl border border-border">
          <img
            src={IMAGES.cityscape}
            alt="Edinburgh cityscape, the heart of our Central Belt service area"
            className="h-72 sm:h-96 w-full object-cover"
            data-testid="service-area-image"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground/70 via-foreground/30 to-transparent" aria-hidden="true" />
          <div className="absolute inset-0 flex items-center">
            <div className="px-8 sm:px-12 max-w-lg">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-primary mb-3">Where we work</p>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white">
                Proudly local to the Central Belt
              </h2>
              <p className="mt-3 text-sm sm:text-base text-white/80 leading-relaxed" data-testid="service-area-text">
                {CONTACT.area}. Remote support wherever you are; on site whenever it counts.
              </p>
            </div>
          </div>
        </div>
      </section>

      <CtaSection />
    </>
  );
}
